import { Search as SearchIcon } from '@mui/icons-material';
import {
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  List,
  TextField,
  Typography,
} from '@mui/material';
import { debounce } from 'lodash';
import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';

export interface SearchProvider {
  name: string;
  fetch: (searchTerm: string, signal?: AbortSignal) => Promise<any[]>;
  render: (
    result: any,
    searchTerm: string,
    handleClose: () => void
  ) => React.ReactNode;
  getOptionLabel?: (result: any) => string;
}

export interface GlobalSearchProps {
  providers: SearchProvider[];
  placeholder?: string;
  width?: string | number;
  maxWidth?: string | number;
}

interface ProviderResults {
  provider: string;
  results: any[];
}

const MIN_QUERY_LENGTH = 2;
const DEBOUNCE_MS = 300;

export const GlobalSearch = ({
  providers,
  placeholder = 'Search...',
  width = '100%',
  maxWidth = 300,
}: GlobalSearchProps) => {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const [results, setResults] = useState<ProviderResults[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchInProgress, setSearchInProgress] = useState(false);

  // Callers commonly build `providers` inline, so its identity changes on every
  // parent render. Reading it through a ref keeps the debounced search stable —
  // otherwise each re-render replaced the debouncer and cancelled the pending
  // keystroke, dropping searches while the user typed.
  const providersRef = useRef(providers);
  providersRef.current = providers;

  const abortRef = useRef<AbortController | null>(null);
  const requestIdRef = useRef(0);

  const handleOpenSearch = useCallback(() => {
    setOpen(true);
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
        event.preventDefault();
        handleOpenSearch();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleOpenSearch]);

  useEffect(() => {
    setOpen(false);
    setQuery('');
    setResults([]);
  }, [location]);

  const runSearch = useCallback(async (searchQuery: string) => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const requestId = ++requestIdRef.current;

    setIsLoading(true);

    const providerResults = await Promise.all(
      providersRef.current.map(async (provider) => {
        try {
          const data = await provider.fetch(searchQuery, controller.signal);
          return { provider: provider.name, results: data };
        } catch (error) {
          if (!controller.signal.aborted) {
            console.error(`Error fetching data from ${provider.name}`, error);
          }
          return { provider: provider.name, results: [] };
        }
      })
    );

    // A newer keystroke already took over; discard this result set entirely.
    if (requestId !== requestIdRef.current) {
      return;
    }

    setResults(providerResults);
    setIsLoading(false);
    setSearchInProgress(false);
  }, []);

  const debouncedSearch = useMemo(
    () => debounce(runSearch, DEBOUNCE_MS),
    [runSearch]
  );

  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length >= MIN_QUERY_LENGTH) {
      setSearchInProgress(true);
      debouncedSearch(query);
      return;
    }

    debouncedSearch.cancel();
    abortRef.current?.abort();
    requestIdRef.current += 1;
    setResults([]);
    setSearchInProgress(false);
    setIsLoading(false);
  }, [query, debouncedSearch]);

  useEffect(
    () => () => {
      debouncedSearch.cancel();
      abortRef.current?.abort();
    },
    [debouncedSearch]
  );

  const handleSearch = (searchQuery: string) => {
    setQuery(searchQuery);
  };

  const handleClose = () => {
    setOpen(false);
    inputRef.current?.blur();
  };

  const hasResults = results.some(
    (providerResults) => providerResults.results.length > 0
  );

  return (
    <>
      <TextField
        variant="outlined"
        placeholder={placeholder}
        size="small"
        sx={{ width, maxWidth }}
        InputProps={{
          ref: inputRef,
          startAdornment: <SearchIcon sx={{ mr: 1 }} />,
          sx: { fontSize: 14, pl: 1 },
        }}
        value={query}
        onChange={(e) => {
          if (!open) setOpen(true);
          handleSearch(e.target.value);
        }}
        onClick={() => setOpen(true)}
      />
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        sx={{
          '& .MuiDialog-container': {
            alignItems: 'flex-start',
          },
        }}
        BackdropProps={{
          style: {
            backgroundColor: 'rgba(255, 255, 255, 0.5)',
            backdropFilter: 'blur(6px)',
          },
        }}
        PaperProps={{
          sx: {
            borderRadius: '12px',
            position: 'relative',
            top: [50, 100, 100, 100],
            marginTop: '0px',
            maxHeight: '80vh',
            overflowY: 'auto',
          },
        }}
      >
        <DialogTitle sx={{ p: 0 }}>
          <Box sx={{ p: 0 }}>
            <TextField
              fullWidth
              variant="standard"
              placeholder={placeholder}
              value={query}
              sx={{
                height: '72px',
                '& .MuiInput-root:before': {
                  borderBottom: 'none !important',
                },
                '& .MuiInput-root:after': {
                  borderBottom: 'none !important',
                },
              }}
              autoFocus
              InputProps={{
                startAdornment: (
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {isLoading ? (
                      <CircularProgress color="primary" size={32} />
                    ) : (
                      <SearchIcon sx={{ fontSize: 32 }} />
                    )}
                  </Box>
                ),
                endAdornment: (
                  <Chip
                    size="small"
                    label="esc"
                    sx={{ mr: 2 }}
                    onClick={handleClose}
                  />
                ),
                sx: {
                  paddingLeft: 2,
                  height: '72px',
                  minHeight: '72px',
                  '& .MuiInputBase-input': {
                    paddingLeft: '16px',
                    fontSize: '24px',
                  },
                },
              }}
              onChange={(e) => handleSearch(e.target.value)}
            />
          </Box>
        </DialogTitle>
        <DialogContent
          sx={{
            p: 0,
            borderTop: '1px solid #e0e0e0',
            borderBottomRightRadius: 4,
            borderBottomLeftRadius: 4,
            bgcolor: 'grey.100',
          }}
        >
          {results.map(
            (providerResults) =>
              providerResults.results.length > 0 && (
                <Box key={providerResults.provider}>
                  <Box
                    sx={{
                      bgcolor: 'grey.100',
                      px: 3,
                      pt: 1,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{ textTransform: 'uppercase' }}
                    >
                      {providerResults.provider}
                    </Typography>
                  </Box>
                  <List
                    sx={{
                      p: 1,
                    }}
                  >
                    {providerResults.results.map((result: any) => (
                      <Fragment key={result.id || result.path || result.url}>
                        {providersRef.current
                          .find((p) => p.name === providerResults.provider)
                          ?.render(result, query, handleClose)}
                      </Fragment>
                    ))}
                  </List>
                </Box>
              )
          )}
          {searchInProgress && !isLoading && !hasResults && (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography color="textSecondary">No results found</Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
